/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import _ from 'lodash';
import Backbone from 'backbone';

import showModal from '@/util/showModal';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import SelectCommandModalView from '../../../login/views/SelectCommandModalView';
import CustomerCommandsGridView from '../CustomerCommandsGridView';
import AddUserCommandAccessByExternalUserIDAndCommandID from '../../../command/services/AddUserCommandAccessByExternalUserIDAndCommandID';
import GetCommandsByExternalUserID from '../../services/GetCommandsByExternalUserID';
import GetApprovedCommandsByExternalUserID from '../../../customer/services/GetApprovedCommandsByExternalUserID';

import tmplCustomerCommandsView from './CustomerCommandsView.hbs';

const CustomerCommandsView = View.extend({
  template: tmplCustomerCommandsView,

  ui: {
    customerCommandGridContainer: '.customer-command-grid-container',
    btnAddCommand: '.btn-add-command'
  },

  regions: {
    customerCommandGridContainer: '@ui.customerCommandGridContainer'
  },

  events: {
    'click @ui.btnAddCommand': 'onClickAddCommand'
  },
  
  childViewEvents: {
    'actingAsCommand:updated': 'updateActingAsCommand'
  },

  onRender() {
    this.renderCustomerCommandGrid();
  },

  renderCustomerCommandGrid() {
    this.customerCommandGridContainer = new CustomerCommandsGridView({
      model: this.model
    });
    this.showChildView('customerCommandGridContainer', this.customerCommandGridContainer);
    this.customerCommandGridContainer.options.collection.fullCollection.reset(this.customerCommandGridContainer.options.collection.fullCollection.models)

    this.$(`#${this.model.get('actingAsCommandId')}`).attr('checked', true).change();
  },

  async onClickAddCommand(e) {
    e?.preventDefault();
    await showModal({
      title: 'Select Command',
      dialogClasses: 'modal-xl',
      view: new SelectCommandModalView({
        model: new Backbone.Model({
          isProfilePage: this.model.get('isProfilePage'),
          isSelfService: this.model.get('isSelfService')
        })
      })
    }).then((commandsToAdd) => {
      const approvedCommands = _.filter(this.customerCommandGridContainer.options.collection.fullCollection.toJSON(), {
        status: 'Approved'
      });
      const commandsAlreadyAdded = _.intersectionBy(approvedCommands, commandsToAdd, 'id');
      const commandString = _.map(commandsAlreadyAdded, 'name').join(', ');

      if (commandString) {
        this.showCommandAlreadyExistsError(commandString);
      }

      commandsToAdd = _.differenceBy(commandsToAdd, approvedCommands, 'id');
      if (commandsToAdd.length) {
        this.saveNewCommands(commandsToAdd);
      }
    });
  },

  updateActingAsCommand() {
    this.customerCommandGridContainer.options.collection.fullCollection.reset(this.customerCommandGridContainer.options.collection.fullCollection.models)
  },

  async saveNewCommands(commandsToAdd) {
    try { 
      const userId = this.model.get('userId').customerId ? this.model.get('userId').customerId : this.model.get('userId');
      await AddUserCommandAccessByExternalUserIDAndCommandID(userId, commandsToAdd);
      this.showAddSuccess();

      const commands = this.model.get('isSelfService') ? await GetApprovedCommandsByExternalUserID(userId) : await GetCommandsByExternalUserID(userId);
      this.model.set('commands', commands);
      this.renderCustomerCommandGrid();
    } catch (err) {
      this.showAddError();
    }
  },

  showAddSuccess() {
    showSuccessMessage({
      code: 'addSuccess',
      title: 'Command Added',
      message: 'Customer successfully added to command.',
      hideAfter: 5
    });
  },

  showCommandAlreadyExistsError(commands) {
    showErrorMessage({
      errorCode: 'addError',
      errorTitle: 'Customer Add Error',
      errorMessage: `Customer already has access to ${commands} command(s). Newly added commands will be saved.`
    });
  },
  showAddError() {
    showErrorMessage({
      errorCode: 'addError',
      errorTitle: 'Customer Add Error',
      errorMessage: 'An error occurred. Try again or contact an administrator if your issue persists.'
    });
  }
});

export default CustomerCommandsView;
